import React from "react";
import { Modal, Button } from "react-bootstrap";
import "./LocationPermissionModal.css";

const LocationPermissionModal = ({
  show,
  onRetry,
  permissionStatus,
  errorMessage,
  isLoading,
}) => {
  const getModalContent = () => {
    switch (permissionStatus) {
      case "denied":
        return {
          title: "Location Permission Required",
          icon: "fa-location-crosshairs",
          iconClass: "denied-icon",
          message:
            "Location services are required to use this application for security and compliance purposes.",
          instructions: [
            "Click on the location icon in your browser's address bar",
            "Select 'Always allow' or enable location permission",
            "Refresh the page and try again",
          ],
          browserSpecific: (
            <div className="browser-instructions mt-3 p-2 bg-light rounded">
              <small className="d-block mb-2">
                <strong>Browser-specific steps:</strong>
              </small>
              <small>
                <strong>Chrome/Edge:</strong> Click the lock icon → Permissions
                → Allow location
              </small>
              <br />
              <small>
                <strong>Firefox:</strong> Click the location icon → Allow
              </small>
              <br />
              <small>
                <strong>Safari:</strong> Preferences → Privacy → Location
              </small>
            </div>
          ),
        };
      case "error":
        return {
          title: "Unable to Access Location",
          icon: "fa-exclamation-triangle",
          iconClass: "error-icon",
          message: errorMessage || "An error occurred while accessing your location.",
          instructions: [
            "Ensure location services are enabled on your device",
            "Check your browser's location permissions",
            "Try again by clicking the Retry button",
          ],
        };
      case "pending":
        return {
          title: "Requesting Location...",
          icon: "fa-hourglass-half",
          iconClass: "pending-icon",
          message: "Please grant location permission in the popup dialog.",
          instructions: [
            "A permission dialog should appear in your browser",
            "Click 'Allow' to enable location services",
          ],
        };
      default:
        return {
          title: "Location Required",
          icon: "fa-location-dot",
          iconClass: "default-icon",
          message: "Location services are required to proceed.",
          instructions: [],
        };
    }
  };

  const content = getModalContent();

  return (
    <Modal
      show={show}
      onHide={() => {}} // Prevent closing by clicking outside
      backdrop="static"
      keyboard={false}
      centered
      className="location-permission-modal"
    >
      <Modal.Header className="bg-light border-bottom">
        <div className="d-flex align-items-center w-100">
          <i className={`fa-solid ${content.icon} ${content.iconClass} me-3`} />
          <Modal.Title className="mb-0">{content.title}</Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="py-4">
        <p className="mb-3 text-muted">{content.message}</p>

        {permissionStatus !== "pending" && content.instructions.length > 0 && (
          <div className="instructions mb-3">
            <small className="d-block mb-2">
              <strong>What to do:</strong>
            </small>
            <ol className="mb-0 ps-3">
              {content.instructions.map((instruction, idx) => (
                <li key={idx} className="mb-2 small">
                  {instruction}
                </li>
              ))}
            </ol>
          </div>
        )}

        {content.browserSpecific}

        <div className="alert alert-info alert-sm mt-3 mb-0" role="alert">
          <small>
            <strong>Why do we need your location?</strong> We use your location
            for security verification, fraud prevention, and compliance with
            local regulations.
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top bg-light">
        {permissionStatus !== "pending" && (
          <Button
            variant="primary"
            onClick={onRetry}
            disabled={isLoading}
            className="w-100"
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Requesting...
              </>
            ) : (
              <>
                <i className="fa-solid fa-redo me-2" />
                Retry
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default LocationPermissionModal;
